/**
 * MagicBI Design System - Input Component
 *
 * Comprehensive input component with validation states,
 * sizes, and accessibility features.
 */

import React, { forwardRef, useState } from "react";
import styled, { css } from "styled-components";
import { typographyMixin } from "../../../tokens/typography";

// Input size types
type InputSize = "sm" | "md" | "lg";
type InputVariant = "default" | "filled" | "outlined";
type InputType =
  | "text"
  | "email"
  | "password"
  | "number"
  | "tel"
  | "url"
  | "search";

// Input props interface
export interface InputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "size"
> {
  /** Input size */
  size?: InputSize;
  /** Input variant style */
  variant?: InputVariant;
  /** Input type */
  type?: InputType;
  /** Error state */
  error?: boolean;
  /** Error message */
  errorMessage?: string;
  /** Success state */
  success?: boolean;
  /** Success message */
  successMessage?: string;
  /** Helper text */
  helperText?: string;
  /** Input label */
  label?: string;
  /** Required field indicator */
  required?: boolean;
  /** Icon to display before input */
  iconBefore?: React.ReactNode;
  /** Icon to display after input */
  iconAfter?: React.ReactNode;
  /** Full width input */
  fullWidth?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Clear button */
  clearable?: boolean;
  /** Clear button callback */
  onClear?: () => void;
}

// Input container props
interface InputContainerProps {
  $size: InputSize;
  $variant: InputVariant;
  $error: boolean;
  $success: boolean;
  $disabled: boolean;
  $focused: boolean;
  $fullWidth: boolean;
  $hasIconBefore: boolean;
  $hasIconAfter: boolean;
}

// Get size styles
const getSizeStyles = (size: InputSize) => {
  switch (size) {
    case "sm":
      return css`
        ${typographyMixin("body", "small")}
        min-height: 32px;
        padding: 6px 12px;

        svg {
          width: 16px;
          height: 16px;
        }
      `;

    case "md":
      return css`
        ${typographyMixin("body", "medium")}
        min-height: 40px;
        padding: 10px 12px;

        svg {
          width: 20px;
          height: 20px;
        }
      `;

    case "lg":
      return css`
        ${typographyMixin("body", "large")}
        min-height: 48px;
        padding: 14px 16px;

        svg {
          width: 24px;
          height: 24px;
        }
      `;

    default:
      return css``;
  }
};

// Get variant styles
const getVariantStyles = (
  variant: InputVariant,
  error: boolean,
  success: boolean,
  disabled: boolean,
  focused: boolean,
) => {
  const baseStyles = css`
    border-radius: ${({ theme }) => theme.borders.semantic.input.default};
    transition:
      ${({ theme }) => theme.transitions.semantic.interactive.input.border},
      ${({ theme }) => theme.transitions.semantic.interactive.input.shadow},
      ${({ theme }) => theme.transitions.semantic.interactive.input.background};
  `;

  switch (variant) {
    case "default":
      return css`
        ${baseStyles}
        background-color: ${({ theme }) => theme.colors.surface.primary};
        border: 1px solid
          ${({ theme }) =>
            error
              ? theme.colors.error[500]
              : success
                ? theme.colors.success[500]
                : focused
                  ? theme.colors.interactive.primary
                  : theme.colors.border.default};

        ${!disabled &&
        css`
          &:hover {
            border-color: ${({ theme }) =>
              error
                ? theme.colors.error[500]
                : success
                  ? theme.colors.success[500]
                  : theme.colors.border.strong};
          }
        `}

        ${focused &&
        css`
          box-shadow: ${({ theme }) => theme.shadows.interactive.focus};
        `}
        
        ${disabled &&
        css`
          background-color: ${({ theme }) => theme.colors.surface.secondary};
          border-color: ${({ theme }) => theme.colors.border.subtle};
          cursor: not-allowed;
        `}
      `;

    case "filled":
      return css`
        ${baseStyles}
        background-color: ${({ theme }) => theme.colors.surface.secondary};
        border: 1px solid transparent;

        ${!disabled &&
        css`
          &:hover {
            background-color: ${({ theme }) => theme.colors.surface.tertiary};
          }
        `}

        ${focused &&
        css`
          background-color: ${({ theme }) => theme.colors.surface.primary};
          border-color: ${({ theme }) =>
            error
              ? theme.colors.error[500]
              : success
                ? theme.colors.success[500]
                : theme.colors.interactive.primary};
          box-shadow: ${({ theme }) => theme.shadows.interactive.focus};
        `}
        
        ${error &&
        css`
          border-color: ${({ theme }) => theme.colors.error[500]};
        `}
        
        ${success &&
        css`
          border-color: ${({ theme }) => theme.colors.success[500]};
        `}
        
        ${disabled &&
        css`
          background-color: ${({ theme }) => theme.colors.surface.secondary};
          cursor: not-allowed;
          opacity: 0.6;
        `}
      `;

    case "outlined":
      return css`
        ${baseStyles}
        background-color: transparent;
        border: 2px solid
          ${({ theme }) =>
            error
              ? theme.colors.error[500]
              : success
                ? theme.colors.success[500]
                : focused
                  ? theme.colors.interactive.primary
                  : theme.colors.border.default};

        ${!disabled &&
        css`
          &:hover {
            border-color: ${({ theme }) =>
              error
                ? theme.colors.error[500]
                : success
                  ? theme.colors.success[500]
                  : theme.colors.border.strong};
          }
        `}

        ${focused &&
        css`
          box-shadow: ${({ theme }) => theme.shadows.interactive.focus};
        `}
        
        ${disabled &&
        css`
          border-color: ${({ theme }) => theme.colors.border.subtle};
          cursor: not-allowed;
          opacity: 0.6;
        `}
      `;

    default:
      return css``;
  }
};

// Input container
const InputContainer = styled.div<InputContainerProps>`
  position: relative;
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;

  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      width: 100%;
    `}

  ${({ $size }) => getSizeStyles($size)}
  ${({ $variant, $error, $success, $disabled, $focused }) =>
    getVariantStyles($variant, $error, $success, $disabled, $focused)}
  
  ${({ $hasIconBefore, $size }) =>
    $hasIconBefore &&
    css`
      padding-left: ${$size === "lg"
        ? "48px"
        : $size === "sm"
          ? "36px"
          : "40px"};
    `}
  
  ${({ $hasIconAfter, $size }) =>
    $hasIconAfter &&
    css`
      padding-right: ${$size === "lg"
        ? "48px"
        : $size === "sm"
          ? "36px"
          : "40px"};
    `}
`;

// Styled input
const StyledInput = styled.input<{
  $hasIconBefore: boolean;
  $hasIconAfter: boolean;
}>`
  /* Reset */
  border: none;
  outline: none;
  background: transparent;
  margin: 0;
  padding: 0;

  /* Layout */
  flex: 1;
  width: 100%;
  min-width: 0;

  /* Typography */
  color: ${({ theme }) => theme.colors.text.primary};

  /* Placeholder */
  &::placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }

  /* Disabled state */
  &:disabled {
    color: ${({ theme }) => theme.colors.text.disabled};
    cursor: not-allowed;

    &::placeholder {
      color: ${({ theme }) => theme.colors.text.disabled};
    }
  }

  /* Remove autofill styling */
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0 1000px
      ${({ theme }) => theme.colors.surface.primary} inset;
    -webkit-text-fill-color: ${({ theme }) => theme.colors.text.primary};
  }

  /* Number input */
  &[type="number"] {
    -moz-appearance: textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }

  /* Search input */
  &[type="search"] {
    &::-webkit-search-decoration,
    &::-webkit-search-cancel-button,
    &::-webkit-search-results-button,
    &::-webkit-search-results-decoration {
      -webkit-appearance: none;
    }
  }
`;

// Icon wrapper
const IconWrapper = styled.div<{
  $position: "before" | "after";
  $size: InputSize;
  $interactive?: boolean;
}>`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: ${({ $interactive }) => ($interactive ? "auto" : "none")};
  color: ${({ theme }) => theme.colors.text.tertiary};

  ${({ $position, $size }) => {
    const offset = $size === "lg" ? "16px" : $size === "sm" ? "12px" : "12px";
    return $position === "before" ? `left: ${offset};` : `right: ${offset};`;
  }}
`;

// Clear button
const ClearButton = styled.button<{ $size: InputSize }>`
  position: absolute;
  right: ${({ $size }) => ($size === "lg" ? "16px" : "12px")};
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ $size }) => ($size === "lg" ? "24px" : "20px")};
  height: ${({ $size }) => ($size === "lg" ? "24px" : "20px")};
  border: none;
  background: none;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.borders.radius.sm};
  color: ${({ theme }) => theme.colors.text.tertiary};
  transition: ${({ theme }) =>
    theme.transitions.semantic.interactive.button.all};

  &:hover {
    color: ${({ theme }) => theme.colors.text.secondary};
    background-color: ${({ theme }) => theme.colors.surface.secondary};
  }

  &:focus {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadows.interactive.focus};
  }
`;

// Form field wrapper
const FormField = styled.div<{ $fullWidth: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;

  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      width: 100%;
    `}
`;

// Label
const Label = styled.label<{ $required: boolean }>`
  ${typographyMixin("label", "medium")}
  color: ${({ theme }) => theme.colors.text.primary};

  ${({ $required }) =>
    $required &&
    css`
      &::after {
        content: " *";
        color: ${({ theme }) => theme.colors.error[500]};
      }
    `}
`;

// Helper text
const HelperText = styled.div<{ $error: boolean; $success: boolean }>`
  ${typographyMixin("caption", "small")}
  color: ${({ theme, $error, $success }) =>
    $error
      ? theme.colors.error.text
      : $success
        ? theme.colors.success.text
        : theme.colors.text.secondary};
`;

// Input component
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      size = "md",
      variant = "default",
      type = "text",
      error = false,
      errorMessage,
      success = false,
      successMessage,
      helperText,
      label,
      required = false,
      iconBefore,
      iconAfter,
      fullWidth = false,
      loading = false,
      clearable = false,
      onClear,
      disabled = false,
      value,
      onChange,
      onFocus,
      onBlur,
      className,
      ...props
    },
    ref,
  ) => {
    const [focused, setFocused] = useState(false);
    const hasValue = value && value.toString().length > 0;
    const showClearButton = clearable && hasValue && !disabled && !loading;
    const adjustedIconAfter = showClearButton ? null : iconAfter;

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(false);
      onBlur?.(e);
    };

    const handleClear = () => {
      onClear?.();
    };

    const inputElement = (
      <InputContainer
        $size={size}
        $variant={variant}
        $error={error}
        $success={success}
        $disabled={disabled}
        $focused={focused}
        $fullWidth={fullWidth}
        $hasIconBefore={!!iconBefore}
        $hasIconAfter={!!adjustedIconAfter || showClearButton}
        className={className}
      >
        {iconBefore && (
          <IconWrapper $position="before" $size={size}>
            {iconBefore}
          </IconWrapper>
        )}

        <StyledInput
          ref={ref}
          type={type}
          value={value}
          disabled={disabled}
          $hasIconBefore={!!iconBefore}
          $hasIconAfter={!!adjustedIconAfter || showClearButton}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          {...props}
        />

        {adjustedIconAfter && (
          <IconWrapper $position="after" $size={size} $interactive>
            {adjustedIconAfter}
          </IconWrapper>
        )}

        {showClearButton && (
          <ClearButton
            type="button"
            $size={size}
            onClick={handleClear}
            tabIndex={-1}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </ClearButton>
        )}
      </InputContainer>
    );

    // If no label or helper text, return just the input
    if (!label && !helperText && !errorMessage && !successMessage) {
      return inputElement;
    }

    return (
      <FormField $fullWidth={fullWidth}>
        {label && (
          <Label $required={required} htmlFor={props.id}>
            {label}
          </Label>
        )}

        {inputElement}

        {(helperText || errorMessage || successMessage) && (
          <HelperText $error={error} $success={success}>
            {errorMessage || successMessage || helperText}
          </HelperText>
        )}
      </FormField>
    );
  },
);

Input.displayName = "Input";

export default Input;
